import React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Box, IconButton, Typography, styled } from "@mui/material";
import { Dividend } from "@rentablo/api/apiv1";
import WarningIcon from "@icons/myicons/warning-circle-filled.svg?react";
import { toCurrency, toGermanNumberFormat } from "@helpers";
import EditIcon from "@icons/myicons/edit.svg?react";
import PlusIcon from "@icons/myicons/plus.svg?react";
import { MobileListRow } from "@components/MobileList";

const CENTS = { minimumFractionDigits: 2 };
const LOTS_FORMAT = { minimumFractionDigits: 0, maximumFractionDigits: 4 };

const MissingIcon = styled(WarningIcon)(({ theme }) => ({
  flex: "none",
  width: 14,
  height: 14,
  color: theme.palette.warning.main,
}));

interface Props {
  dividend: Dividend;
  investmentId: string;
}

export const DividendMobileRow = ({ dividend, investmentId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const date = new Date(dividend.date);
  const lots = dividend.numberOfLots ?? 0;
  const isRecorded = Boolean(dividend.bookingId);
  const canAct = lots > 0;

  const open = () =>
    navigate(
      isRecorded
        ? `/investments/${investmentId}/dividend-wizard/edit/${dividend.bookingId}`
        : `/investments/${investmentId}/dividend-wizard/create?date=${format(date, "MM-dd-yyyy")}`
    );

  return (
    <MobileListRow
      testId="dividend-mobile-row"
      title={
        <>
          <Typography variant="body4" component="div" noWrap>
            {format(date, "dd.MM.yyyy")}
          </Typography>
          {!isRecorded && canAct && (
            <MissingIcon
              role="img"
              aria-label={t("investment.dividends.mobile.missing")}
            />
          )}
        </>
      }
      titleEnd={
        <Typography variant="body4" flex="none" whiteSpace="nowrap">
          {toCurrency(dividend.grossAmount, dividend.currency, false, CENTS)}
        </Typography>
      }
      subtitle={
        <Typography variant="caption" color="textSecondary" noWrap>
          {toGermanNumberFormat(lots, LOTS_FORMAT)} ×{" "}
          {toCurrency(dividend.dividendPerShare, dividend.currency)}
        </Typography>
      }
      // The empty slot keeps the amounts in line with the other rows.
      trailing={
        canAct ? (
          <IconButton
            size="small"
            aria-label={t(
              isRecorded
                ? "domains.dividends.dividendsTable.actions.editDividends"
                : "domains.dividends.dividendsTable.actions.addDividends"
            )}
            data-testid="dividend-action-button"
            onClick={open}
            sx={{ width: 32, height: 32, mr: -1, color: "icon.secondary" }}
          >
            {isRecorded ? (
              <EditIcon width="20px" height="20px" />
            ) : (
              <PlusIcon width="16px" height="16px" />
            )}
          </IconButton>
        ) : (
          <Box aria-hidden width={32} mr={-1} />
        )
      }
    />
  );
};
